import { GridSize } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';

import Button, { ButtonVariant } from '@/components/BaseLibrary/Button/Button';
import FeatureItems from '@/components/HighlightMediaText/FeatureItems/FeatureItems';
import { HighlightIconText } from '@/components/HighlightMediaText/HighlightMediaText';
import Link from '@/components/Link/Link';
import { ButtonContent } from '@/components/OldButton/OldButton';
import QRCode from '@/components/QRCode/QRCode';
import SectionHeader from '@/components/SectionHeader/SectionHeader';
import { Contentful } from '@/contentful';
import AppStore from '@/public/cmsAssets/assets/img/marketplace-buttons/AppStore';
import GooglePlay from '@/public/cmsAssets/assets/img/marketplace-buttons/GooglePlay';
import useGlobalStyles from '@/styles/global.styles';
import { brandColors } from '@/styles/sunrunTheme.styles';

import useStyles from './TextContent.styles';

export interface TextContentAppLinks extends Contentful {
  fields: {
    appStoreLink: string;
    googlePlayLink: string;
    universalRedirectLink: string;
  };
}

export interface TextContentProps {
  subhead?: string;
  features?: HighlightIconText[];
  cta?: ButtonContent[];
  appLinks?: TextContentAppLinks;
  isHeroMode?: boolean;
  eyebrow?: string;
  headline?: string;
  isOnDark: boolean;
  gridSizes?: { lg?: number; md?: number; sm?: number; xs?: number };
  isFixed?: boolean;
  contentClassName?: string;
  subheadClassName?: string;
}

const TextContent = ({
  subhead,
  isOnDark,
  eyebrow,
  features,
  headline,
  cta,
  appLinks,
  isHeroMode,
  gridSizes,
  isFixed = false,
  contentClassName,
  subheadClassName,
}: TextContentProps): React.JSX.Element => {
  const { classes: globalClasses } = useGlobalStyles({ fixed: isFixed });
  const { classes, cx } = useStyles();
  const { lg, md, sm, xs } = {
    lg: gridSizes?.lg || 5,
    md: gridSizes?.md || 4,
    sm: gridSizes?.sm || 7,
    xs: gridSizes?.xs || 12,
  };

  const { appStoreLink, googlePlayLink, universalRedirectLink } =
    appLinks?.fields || {};

  return (
    <Grid2
      lg={lg as GridSize}
      md={md as GridSize}
      sm={sm as GridSize}
      xs={xs as GridSize}
      className={cx(classes.content, contentClassName)}
    >
      <div
        style={{
          color: isOnDark
            ? brandColors.tintsOffWhite40
            : brandColors.tintsNeutral20,
        }}
      >
        <SectionHeader
          eyebrow={eyebrow}
          heading={headline}
          isOnDark={isOnDark}
          className={classes.headline}
          fixed={isFixed}
          isH1={isHeroMode}
        />
      </div>
      {subhead && (
        <div
          style={{
            color: isOnDark
              ? brandColors.tintsOffWhite40
              : brandColors.tintsNeutral20,
          }}
          className={cx(globalClasses.body, classes.body, subheadClassName)}
        >
          {subhead}
        </div>
      )}
      {features && <FeatureItems features={features} isOnDark={isOnDark} />}
      <div className={classes.ctaContainer}>
        {cta &&
          !appLinks &&
          cta.map((button, index) => {
            const isPrimary = index === 0;
            const buttonContent = button?.fields;
            return (
              <Button
                key={'button-id' + index + button?.sys?.id}
                isOnDark={isOnDark}
                variant={
                  isPrimary ? ButtonVariant.SECONDARY : ButtonVariant.TERTIARY
                }
                label={buttonContent?.label}
                href={buttonContent.url}
                className={cx(cta.length > 1 && classes.cta)}
                videoUrl={buttonContent.videoUrl}
              />
            );
          })}
        {appLinks && (
          <div className={classes.qrCodeContainer}>
            <div>
              {universalRedirectLink && (
                <QRCode
                  data={universalRedirectLink}
                  size={120}
                  alt='QR Code'
                  isOnDark={isOnDark}
                />
              )}
            </div>
            <div className={classes.appLinks}>
              <div>
                <Link
                  href={appStoreLink}
                  aria-label='visit apple app store link'
                >
                  <AppStore width={120} name='app-store-icon' />
                </Link>
              </div>
              <div>
                <Link href={googlePlayLink} aria-label='visit google play link'>
                  <GooglePlay width={120} name='google-play-icon' />
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </Grid2>
  );
};

export default TextContent;
